import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import { useTranslation } from "react-i18next";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendUserInfo,
	FrontendUserNotifications,
} from "node-library/dist/portal";
import {
	FrontendContentPage,
	NotificationType,
	Severity,
	SystemAddOn,
	SystemRole,
} from "node-library/dist/constants";
import { CustomIconButton } from "../../common-components/button";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import AddIcon from "@mui/icons-material/Add";

import {
	NeutralColor,
	PrimaryColor,
	SecondaryColor,
} from "node-library/dist/colors";
import {
	Divider,
	ListItem,
	List,
	Popover,
	Typography,
	Tooltip,
	ListItemText,
} from "@mui/material";
import SidebarRow from "./sidebarRow";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import {
	SidebarInfoIcon,
	SidebarOrderIcon,
	SidebarProfileIcon,
	SidebarRosterIcon,
	SidebarSignoutIcon,
} from "../../common-components/sidebarSvgIcons";

import MenuIcon from "@mui/icons-material/Menu";
import {
	hasPermission,
	PermissionKey,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import { doesCompanyHaveAddonsEnabled } from "node-library/dist/helpers/request_helper";

import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { connectSocket, socket } from "../../requestHelper/socketClient";
import { UserIcon } from "../../common-components/svgIcons";
import {
	getNotificationDetailsDisplayText,
	getNotificationHeaderDisplayText,
} from "../../helpers/notificationHelper";
interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	user: FrontendUserInfo | undefined;
	contentPage: FrontendContentPage;
	navigateToPage: (
		link: string,
		contentPage: FrontendContentPage,
		fromPage?: FrontendContentPage,
	) => void;
	signOut: () => void;
}

export default function LandingPageHeaderBar(props: Props) {
	const { t: translation } = useTranslation();
	const { signOut, navigateToPage } = props;

	const { openSnackbar } = useSnackbar();

	const [isMobile, setIsMobile] = useState(false);
	const [notifications, setNotifications] = useState<
		Array<FrontendUserNotifications>
	>([]);

	const unreadCount: number = useMemo(() => {
		// Filter for notifications where 'viewed' (or 'isRead') is false
		// Note: Use the property name that matches your FrontendUserNotifications interface
		return notifications.filter((n) => !n.viewed).length;
	}, [notifications]);

	// To this:
	const [notificationAnchorEl, setNotificationAnchorEl] =
		useState<HTMLDivElement | null>(null);
	const isNotificationOpen = Boolean(notificationAnchorEl);
	const user = props.user;

	useEffect(() => {
		if (user?.userId) {
			// 1. Fetch historical notifications via your existing Axios setup
			const loadInitialData = async () => {
				const response = await sendGETRequest(
					"/notifications/get-user-notifications",
				);

				const userNotifications: Array<FrontendUserNotifications> =
					response.data.notifications;
				setNotifications(userNotifications);
			};
			loadInitialData();

			// 2. Start the real-time listener
			connectSocket(user.userId);

			socket.on("NEW_NOTIFICATION", (newNotif: FrontendUserNotifications) => {
				setNotifications((prev) => [newNotif, ...prev]);
			});
			socket.on(
				"NOTIFICATIONS_MARKED_READ",
				(data: { notificationIds: string[] }) => {
					setNotifications((prev) =>
						prev.map((n) =>
							data.notificationIds.includes(n.notificationId)
								? { ...n, viewed: true }
								: n,
						),
					);
				},
			);

			return () => {
				socket.off("NEW_NOTIFICATION");
				socket.off("NOTIFICATIONS_MARKED_READ"); // Clean up
				// Don't necessarily disconnect here if the header stays mounted
			};
		}
	}, [user?.userId]);

	async function handleNotificationLinkClick(
		notification: FrontendUserNotifications,
	) {
		//mark the notification as read
		if (!notification.viewed) {
			try {
				await sendPOSTRequest("/notifications/mark-as-viewed", {
					notificationIds: [notification.notificationId],
				});
			} catch (err) {
				console.error("Failed to mark notification as viewed", err);
				// You might want to show a snackbar here if the backend fails
			}
		}
		//navigate
		if (notification.link) {
			switch (notification.notificationType) {
				case NotificationType.NEW_WORKFLOW_COMMENT_NOTIFICAITON:
					navigateToPage(
						notification.link,
						FrontendContentPage.BATCH_DETAILS_PAGE,
						props.contentPage,
					);
					break;
			}
		}
	}
	async function handleMarkAllAsRead() {
		const notificationIdsToMark: Array<string> = notifications
			.filter((notification) => {
				return !notification.viewed;
			})
			.map((notification) => {
				return notification.notificationId;
			});

		if (notificationIdsToMark.length == 0) return;
		try {
			await sendPOSTRequest("/notifications/mark-as-viewed", {
				notificationIds: notificationIdsToMark,
			});
		} catch (err) {
			console.error("Failed to mark notification as viewed", err);
			// You might want to show a snackbar here if the backend fails
		}
	}

	const avatarPlaceholder: React.ReactNode = (
		<div
			className={`typography-body-01`}
			style={{ color: SecondaryColor.SECONDARY_200 }}
		>{`${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`}</div>
	);

	const notificationIcon: React.ReactNode = (
		<div
			style={{ position: "relative", display: "flex", cursor: "pointer" }}
			onClick={(e) => {
				setNotificationAnchorEl(e.currentTarget);
			}}
		>
			<NotificationsActiveOutlinedIcon fontSize="small"></NotificationsActiveOutlinedIcon>
			{unreadCount > 0 && (
				<div
					style={{
						position: "absolute",
						top: -10,
						right: -10,
						backgroundColor: "#EF4444", // LIMS-standard Red for attention
						color: "white",
						borderRadius: "10px",
						padding: "0 5px",
						fontSize: "10px",
						fontWeight: "bold",
						border: "2px solid white", // Makes it pop against the sidebar background
						minWidth: "16px",
						textAlign: "center",
					}}
				>
					{unreadCount > 99 ? "99+" : unreadCount}
				</div>
			)}
		</div>
	);
	const notificationTooltip: React.ReactNode = (
		<Popover
			open={isNotificationOpen}
			anchorEl={notificationAnchorEl}
			onClose={() => setNotificationAnchorEl(null)}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "right",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "right",
			}}
			PaperProps={{
				style: { width: "350px", maxHeight: "400px" },
			}}
		>
			<div style={{ padding: "16px" }}>
				<Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
					{translation("Notifications")}
				</Typography>
				<Divider sx={{ my: 1 }} />

				{notifications.map((notification) => {
					const avatarIcon: React.ReactNode = notification.creatorUser
						.avatarUrl ? (
						<img
							src={notification.creatorUser.avatarUrl}
							style={{
								width: "24px",
								height: "24px",
								borderRadius: "50%",
								objectFit: "cover",
							}}
						/>
					) : (
						<UserIcon />
					);
					const notificationHeader: string = getNotificationHeaderDisplayText(
						notification,
						translation,
					);
					const notificationDetails: string = getNotificationDetailsDisplayText(
						notification,
						translation,
					);
					return (
						<ListItem
							component="button"
							onClick={(e) => {
								e.stopPropagation();
								setNotificationAnchorEl(null);
								handleNotificationLinkClick(notification);
							}}
							sx={{
								cursor: "pointer",
								padding: "12px 16px", // Standard spacing
								width: "100%",
								textAlign: "left",
								border: "none",
								backgroundColor: "transparent",
								"&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
							}}
						>
							<div
								className="notification-card"
								style={{
									display: "flex",
									flexDirection: "column",
									width: "100%",
								}}
							>
								<div className="notification-card-title">
									{/* Avatar container */}
									<div style={{ flexShrink: 0 }}>{avatarIcon}</div>

									{/* Header Text + Unread Dot */}
									<div
										className="typography-body-01 typography-bold"
										style={{
											display: "flex",
											alignItems: "baseline",
											gap: "6px",
											flexGrow: 1,
										}}
									>
										{!notification.viewed && (
											<div
												title="Unread"
												style={{
													width: "8px",
													height: "8px",
													borderRadius: "50%",
													backgroundColor: "#EF4444", // Standard LIMS Red
													flexShrink: 0, // Prevents the dot from squishing
												}}
											/>
										)}
										<div>{notificationHeader}</div>
									</div>
								</div>

								{/* Details text - slight indent to align with the header text start */}
								<div
									className={`typography-body-02`}
									style={{ paddingLeft: "32px", color: "#6B7280" }}
								>
									{notificationDetails}
								</div>
							</div>
						</ListItem>
					);
				})}

				<Divider sx={{ my: 1 }} />
				<Typography
					variant="caption"
					sx={{
						cursor: "pointer",
						color: PrimaryColor.PRIMARY_LV5,
						textAlign: "center",
						display: "block",
					}}
					onClick={() => {
						setNotificationAnchorEl(null);
						handleMarkAllAsRead();
					}}
				>
					{translation("MarkAllAsRead")}
				</Typography>
			</div>
		</Popover>
	);

	return (
		<div className="landing-page-header-bar">
			<div className={`ppb-logo sidebar-ppb-logo `}></div>

			<div style={{ display: "flex", gap: "30px", alignItems: "center" }}>
				<div style={{ position: "relative" }}>
					{notificationIcon}
					{notificationTooltip}
				</div>
				<div className={`landing-page-header-bar-user-info`}>
					<div className="sidebar-user-avatar">{avatarPlaceholder}</div>
					<div className="sidebar-username-email-container">
						<div
							className={`typography-body-02`}
							style={{ wordWrap: "break-word" }}
						>{`${user?.firstName} ${user?.lastName}`}</div>
					</div>
				</div>
			</div>
		</div>
	);
}
